import { ref } from 'vue'

// Import separate language files
import zhTranslations from './locales/zh.json'
import enTranslations from './locales/en.json'

// Combine all translations
export const translations = {
    zh: zhTranslations,
    en: enTranslations
}

// Create reactive current language
const currentLanguage = ref('zh')

// Initialize language from localStorage or browser
const initializeLanguage = () => {
    const saved = localStorage.getItem('app-language')
    if (saved && ['zh', 'en'].includes(saved)) {
        currentLanguage.value = saved
        return
    }

    // Auto-detect browser language
    const browserLang = navigator.language.toLowerCase()
    if (browserLang.startsWith('en')) {
        currentLanguage.value = 'en'
    } else {
        currentLanguage.value = 'zh'
    }
}

// Initialize on first load
initializeLanguage()

// Simple i18n hook/composable with reactive language switching
export const useSimpleI18n = () => {
    // Get current language (reactive)
    const getCurrentLanguage = () => {
        return currentLanguage.value
    }

    // Set language and save to localStorage (reactive)
    const setLanguage = (lang) => {
        if (['zh', 'en'].includes(lang)) {
            currentLanguage.value = lang  // This triggers reactivity!
            localStorage.setItem('app-language', lang)
            document.documentElement.setAttribute('lang', lang)
        }
    }

    // Translation function (reactive)
    const t = (key, params = {}) => {
        const keys = key.split('.')
        let value = translations[currentLanguage.value]  // Uses reactive currentLanguage

        for (const k of keys) {
            value = value?.[k]
        }

        if (typeof value === 'string') {
            // Simple parameter replacement
            return Object.keys(params).reduce((str, param) => {
                return str.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param])
            }, value)
        }

        return key // Return key if translation not found
    }

    // Get current language display name (reactive)
    const getCurrentLanguageName = () => {
        return currentLanguage.value === 'zh' ? '中文' : 'English'
    }

    // Check if current language is Chinese (reactive)
    const isChineseLocale = () => {
        return currentLanguage.value === 'zh'
    }

    // Check if current language is English (reactive)
    const isEnglishLocale = () => {
        return currentLanguage.value === 'en'
    }

    // Toggle between Chinese and English
    const toggleLanguage = () => {
        const newLang = currentLanguage.value === 'zh' ? 'en' : 'zh'
        setLanguage(newLang)
        return newLang
    }

    // Switch to specific language
    const switchLanguage = (lang) => {
        if (['zh', 'en'].includes(lang) && lang !== currentLanguage.value) {
            setLanguage(lang)
            return true
        }
        return false
    }

    // Format number for current locale
    const formatNumber = (number, options = {}) => {
        const locale = currentLanguage.value === 'zh' ? 'zh-CN' : 'en-US'
        return new Intl.NumberFormat(locale, options).format(number)
    }

    // Format currency for current locale
    const formatCurrency = (amount, currency = 'CNY') => {
        const locale = currentLanguage.value === 'zh' ? 'zh-CN' : 'en-US'
        const currencyCode = currentLanguage.value === 'zh' ? 'CNY' : currency

        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode
        }).format(amount)
    }

    // Format date for current locale
    const formatDate = (date, options = {}) => {
        const locale = currentLanguage.value === 'zh' ? 'zh-CN' : 'en-US'
        const defaultOptions = currentLanguage.value === 'zh'
            ? { year: 'numeric', month: 'numeric', day: 'numeric' }
            : { year: 'numeric', month: 'short', day: 'numeric' }

        return new Date(date).toLocaleDateString(locale, { ...defaultOptions, ...options })
    }

    // Format date and time for current locale
    const formatDateTime = (dateTime, options = {}) => {
        const locale = currentLanguage.value === 'zh' ? 'zh-CN' : 'en-US'
        const defaultOptions = currentLanguage.value === 'zh'
            ? {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }
            : {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }

        return new Date(dateTime).toLocaleString(locale, { ...defaultOptions, ...options })
    }

    return {
        // Core functions
        t,
        currentLanguage,  // Return the reactive ref directly
        getCurrentLanguage,
        setLanguage,
        switchLanguage,
        toggleLanguage,

        // Utility functions
        getCurrentLanguageName,
        isChineseLocale,
        isEnglishLocale,

        // Formatting functions
        formatNumber,
        formatCurrency,
        formatDate,
        formatDateTime,

        // Access to translations if needed
        translations
    }
}

// Export individual language objects for direct access
export { zhTranslations, enTranslations }

// Export the reactive currentLanguage for direct access
export { currentLanguage }